import { addMonths } from 'date-fns/addMonths';
import { startOfMonth } from 'date-fns/startOfMonth';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { TONES } from '@bloomays-lib/ui.shared/datagrid';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { CoverageMonthSegmentState } from '../../helpers/purchaseOrderDashboard';

const INACTIVE_COLOR = '#C4C4CF';

const segmentBackground = (state: CoverageMonthSegmentState): string => {
  if (state === 'missing') {
    const color = TONES.danger.color;
    return `repeating-linear-gradient(45deg, ${color}, ${color} 2px, ${color}33 2px, ${color}33 4px)`;
  }
  if (state === 'covered') {
    return TONES.success.color;
  }
  if (state === 'expiringSoon') {
    return TONES.warning.color;
  }
  return INACTIVE_COLOR;
};

export type CoverageMonthTimelineProps = {
  segments: CoverageMonthSegmentState[];
  referenceDate: Date;
  monthOffsets: number[];
};

export const CoverageMonthTimeline = ({ segments, referenceDate, monthOffsets }: CoverageMonthTimelineProps) => (
  <Root>
    <Bars>
      {segments.map((state, index) => (
        <Segment key={monthOffsets[index]} $background={segmentBackground(state)} />
      ))}
    </Bars>
    <Labels>
      {monthOffsets.map((offset) => (
        <MonthLabel key={offset} variant="caption">
          {convertDateToStringFormat(startOfMonth(addMonths(referenceDate, offset)), 'MMM yy')}
        </MonthLabel>
      ))}
    </Labels>
  </Root>
);

const Root = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: '100%',
  minWidth: 180,
}));

const Bars = styled('div')(() => ({
  display: 'flex',
  gap: 3,
  width: '100%',
}));

const Segment = styled('span')<{ $background: string }>(({ $background }) => ({
  flex: 1,
  height: 8,
  borderRadius: 3,
  background: $background,
}));

const Labels = styled('div')(() => ({
  display: 'flex',
  gap: 3,
  width: '100%',
}));

const MonthLabel = styled(Typography)(({ theme }) => ({
  flex: 1,
  textAlign: 'center',
  color: theme.palette.neutral?.[60] ?? '#6B6B7E',
  fontSize: 10,
  lineHeight: 1.2,
  textTransform: 'capitalize',
}));
